import {
  afterNextRender,
  Component,
  ElementRef,
  HostListener,
  Injector,
  PLATFORM_ID,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { PageShellComponent } from '@shared/components/page-shell/page-shell.component';
import { LocalizedRouterLinkPipe } from '@core/i18n/localized-router-link.pipe';
import { RouterLink } from '@angular/router';
import { PerguntaService } from './pergunta.service';
import { EditorialSelectComponent } from './editorial-select.component';
import { PromoBannerComponent } from './promo-banner.component';
import {
  shortTitle,
  derivePlatforms,
  visibleQuestions,
  toQueryParams,
} from './nuvem.util';

type SortKey = 'popular' | 'alpha';

/** Itens por página (desktop) e por incremento de scroll (mobile). */
const PAGE_SIZE = 12;
/** Largura (px) abaixo da qual usamos infinite scroll em vez de paginação. */
const MOBILE_MAX_WIDTH = 768;

interface QuestionCard {
  /** Id da variante na listagem (único no track). */
  id: number;
  /** Id da pergunta na API — usado no path `/perguntas/:id`. */
  perguntaId: number;
  short: string;
  platform: string;
  views: number;
  queryParams: Record<string, string>;
}

@Component({
  selector: 'app-nuvem-perguntas-page',
  standalone: true,
  imports: [
    TranslocoPipe,
    PageShellComponent,
    LocalizedRouterLinkPipe,
    RouterLink,
    EditorialSelectComponent,
    PromoBannerComponent,
  ],
  templateUrl: './nuvem-perguntas.page.html',
})
export class NuvemPerguntasPage {
  private readonly service = inject(PerguntaService);
  private readonly transloco = inject(TranslocoService);
  private readonly injector = inject(Injector);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  protected readonly pageSize = PAGE_SIZE;

  /** Recompõe os rótulos ao trocar de idioma. */
  private readonly lang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });
  protected readonly sortOptions = computed(() => {
    this.lang();
    return [
      {
        value: 'popular',
        label: this.transloco.translate('pages.questionCloud.sortPopular'),
      },
      {
        value: 'alpha',
        label: this.transloco.translate('pages.questionCloud.sortAlpha'),
      },
    ];
  });

  protected readonly breadcrumb = [{ labelKey: 'nav.items.questionCloud' }];

  private readonly perguntas = toSignal(this.service.getPerguntas(), {
    initialValue: [],
  });

  protected readonly loading = computed(() => this.perguntas().length === 0);

  protected readonly query = signal('');
  protected readonly active = signal<string | null>(null);
  protected readonly sort = signal<SortKey>('popular');

  protected readonly platforms = computed(() =>
    derivePlatforms(this.perguntas()),
  );
  protected readonly totalCount = computed(
    () => visibleQuestions(this.perguntas()).length,
  );

  protected readonly filtered = computed<QuestionCard[]>(() => {
    const q = this.query().trim().toLowerCase();
    const active = this.active();
    let list = visibleQuestions(this.perguntas()).filter((p) => {
      const platformOk = !active || p.pergunta.sistema === active;
      const textOk = !q || p.texto.toLowerCase().includes(q);
      return platformOk && textOk;
    });

    if (this.sort() === 'popular') {
      list = [...list].sort(
        (a, b) => (b.pergunta.vezesUtilizada ?? 0) - (a.pergunta.vezesUtilizada ?? 0),
      );
    } else {
      list = [...list].sort((a, b) => a.texto.localeCompare(b.texto, 'pt-BR'));
    }

    return list.map((p) => ({
      id: p.id,
      perguntaId: p.pergunta.id,
      short: shortTitle(p),
      platform: p.pergunta.sistema,
      views: p.pergunta.vezesUtilizada ?? 0,
      queryParams: toQueryParams(p.parametros),
    }));
  });

  // ----------------------------- Paginação -------------------------------
  protected readonly isMobile = signal(false);
  /** Página atual (desktop). */
  protected readonly page = signal(1);
  /** Quantidade visível no infinite scroll (mobile). */
  private readonly mobileVisible = signal(PAGE_SIZE);

  private readonly sentinel =
    viewChild<ElementRef<HTMLElement>>('loadMore');
  private readonly questionGrid =
    viewChild<ElementRef<HTMLElement>>('questionGrid');

  protected readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.filtered().length / PAGE_SIZE)),
  );

  /** Cards efetivamente exibidos (fatia da página ou do scroll). */
  protected readonly visibleCards = computed<QuestionCard[]>(() => {
    const list = this.filtered();
    if (this.isMobile()) return list.slice(0, this.mobileVisible());
    const start = (this.page() - 1) * PAGE_SIZE;
    return list.slice(start, start + PAGE_SIZE);
  });

  protected readonly hasMoreMobile = computed(
    () => this.isMobile() && this.mobileVisible() < this.filtered().length,
  );

  private observer?: IntersectionObserver;

  constructor() {
    if (!this.isBrowser) return;
    this.isMobile.set(window.innerWidth < MOBILE_MAX_WIDTH);

    // (Re)liga o observer quando a sentinela entra/sai do DOM (loading ↔ lista).
    effect(() => {
      const el = this.sentinel()?.nativeElement;
      this.observer?.disconnect();
      if (!el || typeof IntersectionObserver === 'undefined') return;
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting) && this.hasMoreMobile()) {
          this.mobileVisible.update((n) => n + PAGE_SIZE);
        }
      });
      this.observer.observe(el);
    });
  }

  @HostListener('window:resize')
  protected onResize(): void {
    if (this.isBrowser) {
      this.isMobile.set(window.innerWidth < MOBILE_MAX_WIDTH);
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private resetPaging(): void {
    this.page.set(1);
    this.mobileVisible.set(PAGE_SIZE);
  }

  protected prevPage(): void {
    this.page.update((p) => Math.max(1, p - 1));
    this.scrollToGridTop();
  }

  protected nextPage(): void {
    this.page.update((p) => Math.min(this.totalPages(), p + 1));
    this.scrollToGridTop();
  }

  private scrollToGridTop(): void {
    // Wait for the page cards to re-render — a smooth scroll started in the
    // same tick as the DOM swap gets cancelled. Also blur the pagination
    // control so the browser doesn't keep it in view and fight the scroll.
    afterNextRender(
      () => {
        const el = this.questionGrid()?.nativeElement;
        if (!el) return;
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        const margin = Number.parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
        const top = el.getBoundingClientRect().top + window.scrollY - margin;
        window.scrollTo({ top, behavior: 'smooth' });
      },
      { injector: this.injector },
    );
  }

  protected setActive(name: string | null): void {
    this.active.set(name);
    this.resetPaging();
  }

  protected onSearch(value: string): void {
    this.query.set(value);
    this.resetPaging();
  }

  protected onSort(value: string): void {
    this.sort.set(value === 'alpha' ? 'alpha' : 'popular');
    this.resetPaging();
  }
}
